import {DropDownList, DropDownListChangeEvent} from "@progress/kendo-react-dropdowns";
import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {VivoPortal} from "@utils/AppConstants";
import languageList = VivoPortal.languageList;
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {usePiwikContext} from "@utils/tracking/PiwikContext";
import {useAuthenticationContext} from "@utils/auth/AuthenticationContext";
import langEn from "../../../images/langs/en.svg";
import langDe from "../../../images/langs/de.svg";
import langPl from "../../../images/langs/pl.svg";
import langFr from "../../../images/langs/fr.svg";
import langEs from "../../../images/langs/es.svg";
import langRu from "../../../images/langs/ru.png";
import langIt from "../../../images/langs/it.svg";
import langTr from "../../../images/langs/tr.svg";
import langPt from "../../../images/langs/pt.svg";
import styles from "./LanguageSelector.module.scss"
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import * as icons from "@fortawesome/free-solid-svg-icons"
import {DropDownButton, DropDownButtonItemClickEvent} from "@progress/kendo-react-buttons";
import {NameWithId} from "@utils/Models";

export default function LanguageSelector() {
    const {state, handlers, utils} = useLanguageSelector();

    return (
        <div className={styles.langSelectorWtrapper}>
            <DropDownButton imageUrl={utils.currentLangIconSrc} className={styles.mobileLanguageSelector}
                            onItemClick={handlers.handleMobileLanguageSelected}
                            items={languageList.map((language) => ({text:language.Name, id:language.Id}))}/>
            <DropDownList className={styles.desktopLanguageSelector} 
                          style={{minWidth: "100px"}} 
                          data={languageList} value={state.lang.get.value}
                          textField={"Name"}
                          dataItemKey={"Id"} 
                          onChange={handlers.handleLanguageSelected}/>
        </div>
    )
}

function useLanguageSelector() {
    const [lang, setLang] = useState({value: languageList[0]});
    const {i18n} = useTranslation();
    const {lng} = useParams<{ lng: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const {setters, trackers} = usePiwikContext();
    const {lang: authLang} = useAuthenticationContext();
    const langIcons: Record<string, string> = {
        en: langEn,
        de: langDe,
        pl: langPl,
        fr: langFr,
        es: langEs,
        ru: langRu,
        it: langIt,
        tr: langTr,
        pt: langPt,
    };

    function handleLanguageSelected(event: DropDownListChangeEvent) {
        let selectedLanguage = event.target.value;
        processLanguageSelection(selectedLanguage);
    }
    function handleMobileLanguageSelected(event: DropDownButtonItemClickEvent) {
        const selectedItem = event.item;
        const selectedLanguage = languageList.filter(x=>x.Id === selectedItem.id)[0];
        processLanguageSelection(selectedLanguage);
    }
    function processLanguageSelection(newLanguage:NameWithId) {
        i18n.changeLanguage(newLanguage.IdString).catch((err) => {
            console.error(err)
        })
        setLang({value: newLanguage});
        authLang.set(newLanguage);
        document.documentElement.lang = newLanguage.IdString;
        const newPathname = lng ? location.pathname.replace(`/${lng}`, `/${newLanguage.IdString}`) : `/${newLanguage.IdString}${location.pathname}`;
        setters.setPiwikScreen("language", newLanguage.IdString);
        trackers.trackVirtualPageView();
        navigate(newPathname);
    }

    useEffect(() => {
        let lang = languageList.filter(x => x.IdString == lng)[0];
        lang ??= languageList[0];
        setLang({value: lang});
        if (authLang.get != lang)
            authLang.set(lang);
        document.documentElement.lang = lang.IdString;
    }, [])

    return {
        state: {
            lang: {get: lang},
        },
        utils: {
            currentLangIconSrc: langIcons[lang.value.IdString] ?? langIcons.en,
        },
        handlers: {
            handleLanguageSelected,
            handleMobileLanguageSelected
        }
    }
}